import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Sparkles, Heart, Users, Trophy, Music, Cake, Gem,
  Wand2, ListChecks, HandHeart, Check, Send, Loader2, Quote,
  MessageSquareHeart, PartyPopper, Star,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi, enquiryApi } from "@/lib/api";
import { formatShortDate } from "@/lib/helpers";
import { eventPath } from "@/lib/eventUrl";

const IMG = {
  hero: "https://images.pexels.com/photos/4662303/pexels-photo-4662303.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1800",
  heroAlt: "https://images.pexels.com/photos/38778506/pexels-photo-38778506.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1800",
  intro: "https://images.unsplash.com/photo-1723832347953-83c28e2d4dd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const CATEGORIES = [
  { id: "weddings", icon: Heart, label: "Weddings", copy: "From intimate ceremonies to grand celebrations, we plan weddings that reflect your story and style.", cta: "Plan a Wedding", img: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { id: "reunions", icon: Users, label: "Reunions", copy: "Bring families, friends and communities together with thoughtfully planned reunion experiences.", cta: "Plan a Reunion", img: "https://images.unsplash.com/photo-1790009336055-d5a3850e1ad3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { id: "sports", icon: Trophy, label: "Sports Events", copy: "From tournaments to fan experiences, we manage sports events with energy, precision and excitement.", cta: "Plan a Sports Event", img: "https://images.unsplash.com/photo-1665413811870-5b29a250f64a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { id: "liveshows", icon: Music, label: "Live Shows", copy: "Concerts, performances and live experiences designed to captivate audiences.", cta: "Plan a Live Show", img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { id: "birthdays", icon: Cake, label: "Birthdays", copy: "From intimate gatherings to spectacular milestone celebrations, make every birthday memorable.", cta: "Plan a Birthday", img: "https://images.unsplash.com/photo-1583875762487-5f8f7c718d14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { id: "anniversaries", icon: Gem, label: "Anniversaries", copy: "Celebrate milestones, memories and togetherness with an experience designed around you.", cta: "Plan an Anniversary", img: "https://images.unsplash.com/photo-1671031990999-77f3bd7e9731?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
];

const WHY = [
  { icon: ListChecks, title: "End-to-End Planning", copy: "From concept and planning to execution and wrap-up." },
  { icon: Wand2, title: "Creative Experiences", copy: "Ideas designed around your occasion, audience and personality." },
  { icon: HandHeart, title: "Seamless Execution", copy: "Professional coordination across vendors, venues, logistics and production." },
  { icon: Sparkles, title: "Attention to Detail", copy: "Because the smallest details often create the biggest memories." },
  { icon: Users, title: "One Team. One Vision.", copy: "A dedicated event partner working with you from beginning to end." },
];

const JOURNEY = [
  { n: "01", t: "Tell Us Your Vision", d: "Share your occasion, ideas and expectations." },
  { n: "02", t: "We Create the Experience", d: "Our team develops the concept, plan and event strategy." },
  { n: "03", t: "We Plan Every Detail", d: "Vendors, venue, décor, production, entertainment, logistics and guest experience." },
  { n: "04", t: "We Execute", d: "Our team manages the event on the ground." },
  { n: "05", t: "You Make the Memories", d: "You enjoy the moment. We take care of the rest." },
];

const PORTFOLIO = [
  { cat: "Weddings", tag: "weddings", img: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "A Garden Vow Renewal" },
  { cat: "Live Shows", tag: "liveshows", img: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "Midsummer Live Nights" },
  { cat: "Sports", tag: "sports", img: "https://images.unsplash.com/photo-1563299796-b729d0af54a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "City Cup Finals" },
  { cat: "Birthdays", tag: "birthdays", img: "https://images.unsplash.com/photo-1741969494307-55394e3e4071?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "A Golden 50th" },
  { cat: "Reunions", tag: "reunions", img: "https://images.unsplash.com/photo-1768776185077-af5d952aba11?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "The Family Gathering" },
  { cat: "Anniversaries", tag: "anniversaries", img: "https://images.unsplash.com/photo-1523521803700-b3bcaeab0150?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "Twenty-Five & Golden" },
  { cat: "Weddings", tag: "weddings", img: "https://images.unsplash.com/photo-1723832347953-83c28e2d4dd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "Candlelit Ballroom" },
  { cat: "Live Shows", tag: "liveshows", img: "https://images.unsplash.com/photo-1565035010268-a3816f98589a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", title: "Headline Festival Stage" },
];
const FILTERS = ["All", "Weddings", "Reunions", "Sports", "Live Shows", "Birthdays", "Anniversaries"];

const RSVP_FEATURES = [
  "Digital Invitations", "RSVP Management", "Guest Lists", "Event Updates", "Event Information",
];

const SERVICES = [
  "Event Concept & Planning", "Event Management", "Venue Coordination", "Décor & Styling",
  "Entertainment", "Artist & Talent Coordination", "Sound & Lighting", "Stage & Production",
  "Guest Management", "Invitations & RSVP", "Vendor Management", "On-Ground Coordination",
  "Logistics & Hospitality", "Photography & Videography",
];

const TESTIMONIALS = [
  { name: "Ananya & Rohan", event: "Wedding", quote: "Godinga turned our wedding into a three-day experience our families still talk about. Every detail was flawless." },
  { name: "The Mehta Family", event: "Reunion", quote: "Fifty relatives, three generations, zero stress. They handled everything so we could just be together." },
  { name: "Vikram Singh", event: "Live Show", quote: "Production, sound, stage — all world-class. The crowd was electric from the first minute." },
  { name: "Priya Nair", event: "Milestone Birthday", quote: "My mother's 60th felt like a film premiere. Godinga understood the emotion, not just the logistics." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    eventsApi.list().then(setEvents).catch(() => {});
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shownWork = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter);

  return (
    <div className="min-h-screen bg-background">
      <Navbar transparent onPlan={() => scrollTo("plan")} />

      {/* 1. HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.hero})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-black/40" />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-24 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl text-white">
            <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-gold backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> Event Management & Experience Company
            </span>
            <h1 className="mt-7 font-serif text-5xl font-semibold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl">
              YOUR EVENT.<br />OUR EXPERIENCE.<br /><span className="italic text-gold">UNFORGETTABLE MOMENTS.</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/80">
              Godinga is an event management company creating, planning and executing celebrations and experiences that people remember.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button data-testid="hero-plan-btn" onClick={() => scrollTo("plan")} className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-base font-semibold text-primary transition-all hover:shadow-2xl hover:shadow-gold/30">
                Plan Your Event <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button data-testid="hero-explore-btn" onClick={() => scrollTo("events")} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur transition-colors hover:bg-white/15">
                Explore Our Events
              </button>
            </div>
            <button data-testid="hero-rsvp-link" onClick={() => scrollTo("rsvp")} className="mt-6 inline-flex items-center gap-1.5 text-sm text-white/60 underline-offset-4 hover:text-white hover:underline">
              Manage Your RSVP <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* 2. INTRO */}
      <section id="about" className="mx-auto max-w-7xl scroll-mt-20 px-5 py-24 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow text-gold">More than an event</p>
            <h2 className="mt-3 font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
              More Than an Event.<br /><span className="italic text-gold">We Create an Experience.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              At Godinga, we believe every event has a story to tell. From the first idea to the final guest goodbye, we take care of the details that turn an occasion into an experience.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Whether it's a dream wedding, a family reunion, a high-energy sports event, a live show, a milestone birthday or an anniversary celebration, our team brings creativity, planning and flawless execution together under one roof.
            </p>
            <button data-testid="discover-btn" onClick={() => scrollTo("why")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">
              Discover Godinga <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-tr from-gold/20 to-transparent blur-2xl" />
            <img src={IMG.intro} alt="A Godinga celebration" className="aspect-[4/5] w-full rounded-3xl object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      {/* 3. CATEGORIES */}
      <section id="events" className="scroll-mt-20 border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-gold">What we do</p>
            <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Events We Bring to Life</h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                className="group relative overflow-hidden rounded-3xl" data-testid={`category-${c.id}`}>
                <div className="aspect-[4/5] w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${c.img})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                  <c.icon className="h-7 w-7 text-gold" />
                  <h3 className="mt-3 font-serif text-2xl font-semibold">{c.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/80">{c.copy}</p>
                  <button onClick={() => scrollTo("plan")} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold opacity-90 transition-opacity hover:opacity-100">
                    {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. WHY GODINGA */}
      <section id="why" className="mx-auto max-w-7xl scroll-mt-20 px-5 py-24 sm:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow text-gold">The Godinga difference</p>
          <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Why Godinga?</h2>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <motion.div key={w.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.07 }}
              className={`rounded-3xl border border-border p-8 transition-all hover:-translate-y-1 hover:shadow-lg ${i === 0 ? "bg-primary text-primary-foreground lg:row-span-1" : "bg-card"}`}>
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${i === 0 ? "bg-white/10" : "bg-secondary"} text-gold`}>
                <w.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold">{w.title}</h3>
              <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-white/75" : "text-muted-foreground"}`}>{w.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. JOURNEY */}
      <section className="border-y border-border bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow text-gold">Our event journey</p>
            <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">From Idea to Unforgettable</h2>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-5">
            {JOURNEY.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="relative">
                <span className="font-serif text-4xl font-semibold text-gold">{s.n}</span>
                <div className="mt-3 h-px w-full bg-white/15" />
                <h3 className="mt-4 text-base font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-white/70">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. PORTFOLIO */}
      <section id="work" className="mx-auto max-w-7xl scroll-mt-20 px-5 py-24 sm:px-8">
        <div className="flex flex-col items-center text-center">
          <p className="eyebrow text-gold">Our work</p>
          <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Moments We've Created</h2>
          <p className="mt-4 max-w-xl text-sm text-muted-foreground">A glimpse of the experiences we've brought to life. Replace these with your own event photography anytime.</p>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
              {f}
            </button>
          ))}
        </div>
        <div className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
          {shownWork.map((p, i) => (
            <motion.div key={p.title + i} initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              className="group relative break-inside-avoid overflow-hidden rounded-2xl" data-testid="work-item">
              <img src={p.img} alt={p.title} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 to-transparent p-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="eyebrow text-gold">{p.cat}</span>
                <p className="font-serif text-xl font-semibold text-white">{p.title}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 7. RSVP — supporting feature */}
      <section id="rsvp" className="scroll-mt-20 border-y border-border bg-secondary/30">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div>
            <p className="eyebrow text-gold">Guest & RSVP technology</p>
            <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Your Event, Fully Connected</h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Planning is only part of the experience. Godinga also helps you keep your guests connected with digital invitations, RSVPs, event information and updates.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {RSVP_FEATURES.map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium">
                  <Check className="h-4 w-4 text-gold" /> {f}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="rsvp-manage-btn" onClick={() => navigate("/login")} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors hover:bg-secondary">
                Guest Management <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          {events.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {events.slice(0, 2).map((ev) => (
                <Link key={ev.id} to={eventPath(ev)} data-testid={`live-event-${ev.id}`} className="group overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative h-40 bg-cover bg-center" style={{ backgroundImage: `url(${ev.cover_image})` }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 text-white">
                      <p className="eyebrow text-gold">{formatShortDate(ev.date)}</p>
                      <p className="font-serif text-lg font-semibold leading-tight">{ev.title}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 text-sm">
                    <span className="text-muted-foreground">Live event page</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-gold">View <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-border bg-card p-10 text-center">
              <MessageSquareHeart className="mx-auto h-10 w-10 text-gold" />
              <p className="mt-4 font-serif text-xl font-medium">Digital event pages & RSVPs</p>
              <p className="mt-2 text-sm text-muted-foreground">Every Godinga event comes with its own connected guest experience.</p>
            </div>
          )}
        </div>
      </section>

      {/* 8. SERVICES strip */}
      <section id="services" className="mx-auto max-w-7xl scroll-mt-20 px-5 py-24 sm:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow text-gold">Full-service event company</p>
          <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Everything Under One Roof</h2>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          {SERVICES.map((s) => (
            <span key={s} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:border-gold/50">
              <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {s}
            </span>
          ))}
        </div>
      </section>

      {/* 9. TESTIMONIALS */}
      <section className="border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow text-gold">Stories from our clients</p>
            <h2 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Loved by the People We Celebrate</h2>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.07 }}
                className="flex flex-col rounded-3xl border border-border bg-card p-7">
                <Quote className="h-7 w-7 text-gold" />
                <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 font-serif text-lg font-semibold text-gold">{t.name.charAt(0)}</span>
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.event}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. PLAN YOUR EVENT — enquiry */}
      <section id="plan" className="relative scroll-mt-20 overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold/20 blur-3xl" />
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <PartyPopper className="h-10 w-10 text-gold" />
            <h2 className="mt-5 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Have an Event in Mind?</h2>
            <p className="mt-5 text-lg text-white/75">Tell us what you're celebrating. We'll help you turn it into an experience.</p>
            <div className="mt-8 space-y-3 text-sm text-white/70">
              <p className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Free consultation & concept session</p>
              <p className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Dedicated event partner from day one</p>
              <p className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Response within 24 hours</p>
            </div>
          </div>
          <EnquiryForm />
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="mx-auto max-w-5xl px-5 py-24 text-center sm:px-8">
        <Star className="mx-auto h-8 w-8 text-gold" />
        <h2 className="mt-6 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Let's Create Something Worth Remembering.</h2>
        <p className="mt-5 text-lg text-muted-foreground">Whatever you're celebrating, Godinga is here to bring the vision to life.</p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <button data-testid="final-plan-btn" onClick={() => scrollTo("plan")} className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground transition-all hover:shadow-xl">
            Plan Your Event <ArrowRight className="h-5 w-5" />
          </button>
          <button onClick={() => scrollTo("plan")} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-8 py-4 text-base font-semibold transition-colors hover:bg-secondary">
            Talk to Godinga
          </button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-10 sm:flex-row sm:px-8">
          <div>
            <span className="font-serif text-xl font-semibold">God<span className="text-gold">inga</span></span>
            <p className="mt-1 text-sm text-muted-foreground">We create experiences worth celebrating.</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <button onClick={() => scrollTo("about")} className="hover:text-foreground">About</button>
            <button onClick={() => scrollTo("events")} className="hover:text-foreground">Events</button>
            <button onClick={() => scrollTo("work")} className="hover:text-foreground">Our Work</button>
            <Link to="/login" className="hover:text-foreground">Guest Management</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_type: "Wedding", preferred_date: "", location: "", guest_count: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

  const submit = async () => {
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error("Please add your name and an email or phone");
      return;
    }
    setSending(true);
    try {
      await enquiryApi.create(form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch within 24 hours!");
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl bg-white/5 p-10 text-center" data-testid="enquiry-success">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-8 w-8" /></span>
        <h3 className="mt-6 font-serif text-2xl font-semibold text-white">Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}!</h3>
        <p className="mt-3 text-white/70">Your enquiry is with our event team. We'll reach out within 24 hours to start shaping your experience.</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white/5 p-6 backdrop-blur sm:p-8" data-testid="enquiry-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid="enquiry-name" className={field} placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <input data-testid="enquiry-phone" className={field} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        <input data-testid="enquiry-email" type="email" className={field} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        <select data-testid="enquiry-event-type" className={field} value={form.event_type} onChange={(e) => set("event_type", e.target.value)}>
          {["Wedding", "Reunion", "Sports Event", "Live Show", "Birthday", "Anniversary", "Corporate", "Other"].map((o) => <option key={o} className="bg-primary">{o}</option>)}
        </select>
        <input data-testid="enquiry-date" type="date" className={field} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
        <input data-testid="enquiry-location" className={field} placeholder="Location" value={form.location} onChange={(e) => set("location", e.target.value)} />
        <input data-testid="enquiry-guests" className={field} placeholder="Estimated guest count" value={form.guest_count} onChange={(e) => set("guest_count", e.target.value)} />
        <input data-testid="enquiry-budget" className={field} placeholder="Approximate budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} />
      </div>
      <textarea data-testid="enquiry-message" rows={3} className={`${field} mt-4`} placeholder="Tell us about your event" value={form.message} onChange={(e) => set("message", e.target.value)} />
      <button data-testid="enquiry-submit" onClick={submit} disabled={sending} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-base font-semibold text-primary transition-all hover:shadow-xl disabled:opacity-60">
        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {sending ? "Sending..." : "Start Planning With Godinga"}
      </button>
    </div>
  );
}
